import {
  all,
  always,
  any,
  anyPass,
  filter,
  has,
  ifElse,
  isEmpty,
  isNil,
  join,
  keys,
  map,
  pipe,
  uniq,
  values,
} from 'ramda';
import { contained, isFalse, isTrue } from 'ramda-adjunct';
import { IntlFormatters } from 'react-intl';
import { MEDIA_TYPE_TRANSLATIONS, MediaType } from '../constants/MediaType';
import { NameSpace } from '../constants/NameSpace';
import { TMediaTypes } from './hoc/withMediaTypes/withMediaTypes.types';
import { Targets } from './hooks/bi/useTrackSearchAndFiltersAnalytics/useTrackSearchAndFiltersAnalytics.types';

export const mediaTypesArrayToTranslatedArray = (
  mediaTypes: MediaType[],
  formatMessage: IntlFormatters['formatMessage'],
): string[] =>
  pipe<MediaType[], string[], string[], string[]>(
    map((mediaType) => MEDIA_TYPE_TRANSLATIONS[mediaType]),
    uniq,
    map((translationKey) => formatMessage({ id: translationKey })),
  )(mediaTypes);

export const isMediaTypeNotProvided = (mediaType: string): boolean => isEmpty(mediaType) || isNil(mediaType);

export const getAcceptedMediaTypesByNameSpace = pipe<NameSpace, MediaType[], string>((namespace) => {
  switch (namespace) {
    case NameSpace.WixMusic:
      return [MediaType.SecureMusic];

    case NameSpace.WixVideo:
      return [MediaType.SecureVideo];

    case NameSpace.ArtStore:
      return [MediaType.SecurePicture];

    case NameSpace.DigitalProduct:
      return [MediaType.SecurePicture, MediaType.SecureMusic, MediaType.SecureDocument, MediaType.SecureZip];

    case NameSpace.All:
      return [MediaType.All];

    default:
      return [
        MediaType.Picture,
        MediaType.Video,
        MediaType.Music,
        MediaType.ThreeD,
        MediaType.Document,
        MediaType.VectorArt,
        MediaType.SiteIcon,
        MediaType.SWF,
      ];
  }
}, join(','));

export const isSecureMediaType = contained<string | MediaType>([
  MediaType.SecurePicture,
  MediaType.SecureMusic,
  MediaType.SecureVideo,
  MediaType.SecureDocument,
  MediaType.SecureZip,
]);

export const isVideoMediaType = contained([MediaType.Video, MediaType.SecureVideo]);

export const areAllMediaTypesEnabled = pipe<TMediaTypes, boolean[], boolean>(values, all(isTrue));

export const areAllMediaTypesDisabled = pipe<TMediaTypes, boolean[], boolean>(values, all(isFalse));

export const areNotAllMediaTypesEnabled = pipe<TMediaTypes, boolean[], boolean>(values, any(isFalse));

export const areNotAllMediaTypesDisabled = pipe<TMediaTypes, boolean[], boolean>(values, any(isTrue));

export const isMediaTypeEnabled = (mediaType: MediaType, mediaTypes: TMediaTypes) => {
  if (areAllMediaTypesDisabled(mediaTypes)) {
    return has(mediaType, mediaTypes);
  }

  return Boolean(mediaTypes[mediaType]);
};

export const stringifiedEnabledMediaTypes = ifElse<TMediaTypes, string, string>(
  anyPass([areAllMediaTypesDisabled, areAllMediaTypesEnabled]),
  always(Targets.All),
  pipe(filter(Boolean), keys, join(',')),
);
